import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  ArrowRightLeft,
  Layers,
  RotateCcw,
  X,
  CheckCircle2,
  AlertCircle,
  Plus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { money } from "../lib/api";
import {
  getAllocationForLine,
  savePeriodAllocation,
  removePeriodAllocation,
  formatMonthLabel,
} from "../lib/periodAllocations";

const FY_MONTHS = [
  "2026-04",
  "2026-05",
  "2026-06",
  "2026-07",
  "2026-08",
  "2026-09",
  "2026-10",
  "2026-11",
  "2026-12",
  "2027-01",
  "2027-02",
  "2027-03",
];

function getPreviousMonth(ym) {
  if (!ym) return "2026-09";
  const [y, m] = ym.split("-").map(Number);
  const prevDate = new Date(y, m - 2, 1);
  const py = prevDate.getFullYear();
  const pm = String(prevDate.getMonth() + 1).padStart(2, "0");
  return `${py}-${pm}`;
}

function getNextMonth(ym) {
  if (!ym) return "2026-10";
  const [y, m] = ym.split("-").map(Number);
  const nextDate = new Date(y, m, 1);
  const ny = nextDate.getFullYear();
  const nm = String(nextDate.getMonth() + 1).padStart(2, "0");
  return `${ny}-${nm}`;
}

export default function PeriodAllocationModal({
  isOpen,
  onClose,
  line,
  ledgers = [],
  onSave,
}) {
  const realLineId = line?.parent_id || (line?.id ? line.id.split("__alloc__")[0] : "");
  const lineAmount = line?.total_original_amount || ((line?.debit || 0) > 0 ? line?.debit : (line?.credit || 0));
  const isDebit = (line?.debit || 0) > 0;
  const originalDate = line?.original_date || line?.date || "";
  const originalMonth = line?.original_month || (originalDate || "").slice(0, 7) || "2026-10";

  // Existing allocation or clean default
  const existing = useMemo(() => (realLineId ? getAllocationForLine(realLineId) : null), [realLineId]);

  const [mode, setMode] = useState("shift"); // "shift" | "split"
  const [targetMonth, setTargetMonth] = useState("2026-09");
  const [splitRows, setSplitRows] = useState([]);
  const [selectedLedgerId, setSelectedLedgerId] = useState("");
  const [selectedLedgerName, setSelectedLedgerName] = useState("");
  const [note, setNote] = useState("");

  // Sync state whenever line or isOpen changes
  useEffect(() => {
    if (!line || !isOpen) return;
    const curAlloc = getAllocationForLine(realLineId);
    const origM = originalMonth;
    const amt = lineAmount;

    if (curAlloc) {
      setMode(curAlloc.type || "shift");
      setTargetMonth(
        curAlloc.target_month || curAlloc.allocations?.[0]?.month || getPreviousMonth(origM)
      );
      if (curAlloc.type === "split" && curAlloc.allocations?.length) {
        setSplitRows(
          curAlloc.allocations.map((a) => ({
            month: a.month,
            amount: String(a.amount),
          }))
        );
      } else {
        const m1 = origM;
        const m2 = getNextMonth(m1);
        const m3 = getNextMonth(m2);
        const part = Number((amt / 3).toFixed(2));
        const remainder = Number((amt - part * 2).toFixed(2));
        setSplitRows([
          { month: m1, amount: String(part) },
          { month: m2, amount: String(part) },
          { month: m3, amount: String(remainder) },
        ]);
      }
      setSelectedLedgerId(curAlloc.ledger_id || line.ledger_id || "");
      setSelectedLedgerName(curAlloc.ledger_name || line.ledger_name || "");
      setNote(curAlloc.note || "");
    } else {
      setMode("shift");
      setTargetMonth(getPreviousMonth(origM));
      const m1 = origM;
      const m2 = getNextMonth(m1);
      const m3 = getNextMonth(m2);
      const part = Number((amt / 3).toFixed(2));
      const remainder = Number((amt - part * 2).toFixed(2));
      setSplitRows([
        { month: m1, amount: String(part) },
        { month: m2, amount: String(part) },
        { month: m3, amount: String(remainder) },
      ]);
      setSelectedLedgerId(line.ledger_id || "");
      setSelectedLedgerName(line.ledger_name || "");
      setNote("");
    }
  }, [line, isOpen, realLineId, originalMonth, lineAmount]);

  // Calculations for Split mode
  const splitTotal = useMemo(() => {
    return splitRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  }, [splitRows]);

  const splitRemaining = Number((lineAmount - splitTotal).toFixed(2));
  const isSplitBalanced = Math.abs(splitRemaining) <= 0.05;

  // Update ledger name when ledger id changes
  const handleLedgerChange = (e) => {
    const lid = e.target.value;
    setSelectedLedgerId(lid);
    const found = ledgers.find((l) => l.id === lid);
    if (found) {
      setSelectedLedgerName(found.name);
    } else {
      setSelectedLedgerName("");
    }
  };

  if (!isOpen || !line) return null;

  // Auto divide evenly across split rows
  const distributeEvenly = (rowsToDivide = splitRows) => {
    const count = rowsToDivide.length;
    if (count === 0) return;
    const base = Math.floor((lineAmount / count) * 100) / 100;
    const totalBase = Number((base * (count - 1)).toFixed(2));
    const lastAmt = Number((lineAmount - totalBase).toFixed(2));

    const updated = rowsToDivide.map((r, i) => ({
      ...r,
      amount: String(i === count - 1 ? lastAmt : base),
    }));
    setSplitRows(updated);
  };

  // Quick preset apply for split
  const applyPresetCount = (count, forward = true) => {
    let months = [];
    if (forward) {
      let cur = originalMonth;
      months = [cur];
      for (let i = 1; i < count; i++) {
        cur = getNextMonth(cur);
        months.push(cur);
      }
    } else {
      let cur = originalMonth;
      months = [cur];
      for (let i = 1; i < count; i++) {
        cur = getPreviousMonth(cur);
        months.unshift(cur);
      }
    }
    const newRows = months.map((m) => ({ month: m, amount: "0" }));
    distributeEvenly(newRows);
  };

  const addSplitRow = () => {
    const lastRow = splitRows[splitRows.length - 1];
    const nextM = lastRow ? getNextMonth(lastRow.month) : originalMonth;
    const updated = [...splitRows, { month: nextM, amount: "0" }];
    setSplitRows(updated);
  };

  const removeSplitRow = (idx) => {
    if (splitRows.length <= 1) return;
    const updated = splitRows.filter((_, i) => i !== idx);
    setSplitRows(updated);
  };

  const handleSplitMonthChange = (idx, val) => {
    const updated = [...splitRows];
    updated[idx].month = val;
    setSplitRows(updated);
  };

  const handleSplitAmountChange = (idx, val) => {
    const updated = [...splitRows];
    updated[idx].amount = val;
    setSplitRows(updated);
  };

  // Save handler
  const handleSave = () => {
    let allocations = [];
    if (mode === "shift") {
      if (!targetMonth) {
        toast.error("Please pick a target month");
        return;
      }
      allocations = [
        {
          month: targetMonth,
          amount: lineAmount,
          pct: 100,
        },
      ];
    } else {
      // Split mode
      if (!isSplitBalanced) {
        toast.error(
          `Split amounts must equal exactly ${money(lineAmount)}. Remaining: ${money(splitRemaining)}`
        );
        return;
      }
      // Check for empty months
      if (splitRows.some((r) => !r.month)) {
        toast.error("All split rows must have a valid month selected");
        return;
      }
      allocations = splitRows.map((r) => {
        const amt = parseFloat(r.amount) || 0;
        return {
          month: r.month,
          amount: Number(amt.toFixed(2)),
          pct: Number(((amt / lineAmount) * 100).toFixed(1)),
        };
      });
    }

    const payload = {
      type: mode,
      line_id: realLineId,
      original_date: originalDate,
      original_month: originalMonth,
      narration: line.narration,
      total_amount: lineAmount,
      is_debit: isDebit,
      ledger_id: selectedLedgerId,
      ledger_name: selectedLedgerName || "Misc. Expenses",
      allocations,
      target_month: mode === "shift" ? targetMonth : undefined,
      note: note.trim(),
    };

    savePeriodAllocation(realLineId, payload);
    toast.success(
      mode === "shift"
        ? `Shifted 100% to ${formatMonthLabel(targetMonth)}`
        : `Allocated across ${allocations.length} months`
    );
    if (onSave) onSave(payload);
    onClose();
  };

  // Reset handler
  const handleReset = () => {
    removePeriodAllocation(realLineId);
    toast.success("Allocation cleared. Transaction reverted to bank date.");
    if (onSave) onSave(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-150">
      <div
        className="lss-card bg-white w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-5 sm:px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-amber-950 text-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-black tracking-wide text-white">
                Shift / Allocate Period
              </h2>
              <p className="text-[11px] text-amber-200/90 font-medium">
                Re-allocate payment across months for accurate P&amp;L matching
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-5">
          {/* Selected Line Card */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2 pb-2 border-b border-slate-200/80">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-500 uppercase">
                  Bank Date:
                </span>
                <span className="text-xs font-extrabold text-slate-900 tabular">
                  {line.date}
                </span>
                <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300">
                  {formatMonthLabel(originalMonth)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${
                    isDebit
                      ? "bg-rose-50 text-rose-700 border-rose-200"
                      : "bg-emerald-50 text-emerald-700 border-emerald-200"
                  }`}
                >
                  {isDebit ? "Debit (Expense)" : "Credit (Income)"}
                </span>
                <span className="text-sm font-black tabular text-slate-950">
                  {money(lineAmount)}
                </span>
              </div>
            </div>

            <div className="text-xs font-semibold text-slate-800 line-clamp-2" title={line.narration}>
              {line.narration}
              {line.ref_no && (
                <span className="ml-1 text-slate-400 font-normal">[{line.ref_no}]</span>
              )}
            </div>

            {/* Ledger Tag / Selector */}
            <div className="mt-2.5 pt-2 border-t border-slate-200/60 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                Assigned Ledger:
              </span>
              <div className="flex-1 max-w-xs">
                {ledgers.length > 0 ? (
                  <select
                    value={selectedLedgerId}
                    onChange={handleLedgerChange}
                    className="w-full text-xs font-semibold text-slate-800 bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                  >
                    <option value="">(No specific ledger - Misc. Expenses)</option>
                    {ledgers.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.name} ({l.grp})
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs font-extrabold text-slate-900">
                    {selectedLedgerName || "Misc. Expenses"}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Mode Selector Tabs */}
          <div>
            <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-2">
              Choose Allocation Method:
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={() => setMode("shift")}
                className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                  mode === "shift"
                    ? "border-amber-500 bg-amber-50/70 shadow-xs ring-1 ring-amber-400"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50"
                }`}
              >
                <div
                  className={`p-2 rounded-lg ${
                    mode === "shift"
                      ? "bg-amber-500 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <ArrowRightLeft className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-black text-slate-900">
                    Shift 100% to Another Month
                  </div>
                  <div className="text-[11px] font-medium text-slate-500 mt-0.5 leading-snug">
                    Move full expense to another month (e.g. Sep expense paid in Oct).
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setMode("split")}
                className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                  mode === "split"
                    ? "border-indigo-500 bg-indigo-50/70 shadow-xs ring-1 ring-indigo-400"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/50"
                }`}
              >
                <div
                  className={`p-2 rounded-lg ${
                    mode === "split"
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  <Layers className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xs font-black text-slate-900">
                    Split Across Multiple Months
                  </div>
                  <div className="text-[11px] font-medium text-slate-500 mt-0.5 leading-snug">
                    Divide lump-sum payment (e.g. 2, 3 or 4 months quarterly rent/AMC).
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Mode 1: Shift 100% Form */}
          {mode === "shift" && (
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/40 space-y-3.5 animate-in fade-in duration-100">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-xs font-black text-slate-800 uppercase tracking-wider">
                  Target Month for 100% of {money(lineAmount)}:
                </label>
                <span className="text-[11px] font-semibold text-amber-900">
                  Currently: {formatMonthLabel(originalMonth)}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">
                    Select Target Financial Month:
                  </label>
                  <select
                    value={targetMonth}
                    onChange={(e) => setTargetMonth(e.target.value)}
                    className="w-full text-xs font-bold text-slate-900 bg-white border border-amber-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    {FY_MONTHS.map((m) => (
                      <option key={m} value={m}>
                        {formatMonthLabel(m)} {m === originalMonth ? "(Bank Statement Month)" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">
                    Or pick any calendar month:
                  </label>
                  <input
                    type="month"
                    value={targetMonth}
                    onChange={(e) => setTargetMonth(e.target.value)}
                    className="w-full text-xs font-bold text-slate-900 bg-white border border-amber-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              {/* Dynamic Impact Summary Box */}
              <div className="p-3 bg-white/90 border border-amber-300 rounded-lg text-xs space-y-1.5">
                <div className="font-bold text-amber-950 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-amber-600" />
                  <span>P&amp;L Movement Impact:</span>
                </div>
                <div className="text-[11px] text-slate-700 pl-5 space-y-1">
                  <div>
                    • <strong className="text-rose-700">Excluded from {formatMonthLabel(originalMonth)}:</strong>{" "}
                    Deducts {money(lineAmount)} from {formatMonthLabel(originalMonth)}'s{" "}
                    <em>{selectedLedgerName || "Operational Expenses"}</em>.
                  </div>
                  <div>
                    • <strong className="text-emerald-700">Added to {formatMonthLabel(targetMonth)}:</strong>{" "}
                    Adds {money(lineAmount)} to {formatMonthLabel(targetMonth)}'s{" "}
                    <em>{selectedLedgerName || "Operational Expenses"}</em>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mode 2: Multi-Month Split Form */}
          {mode === "split" && (
            <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/40 space-y-4 animate-in fade-in duration-100">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-black text-slate-800 uppercase tracking-wider">
                  Divide {money(lineAmount)} Across Months:
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] font-bold text-slate-500 uppercase mr-1">
                    Presets:
                  </span>
                  <button
                    type="button"
                    onClick={() => applyPresetCount(3, true)}
                    className="px-2 py-0.5 text-[10px] font-extrabold uppercase rounded bg-indigo-100 hover:bg-indigo-200 border border-indigo-300 text-indigo-950 cursor-pointer"
                    title="Forward 3 months starting from transaction month (e.g. Sep, Oct, Nov)"
                  >
                    3 Mo Forward
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPresetCount(3, false)}
                    className="px-2 py-0.5 text-[10px] font-extrabold uppercase rounded bg-white hover:bg-indigo-50 border border-indigo-300 text-indigo-900 cursor-pointer"
                    title="Prior 3 months ending in transaction month (e.g. Jul, Aug, Sep)"
                  >
                    3 Mo Past
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPresetCount(2, true)}
                    className="px-2 py-0.5 text-[10px] font-extrabold uppercase rounded bg-white hover:bg-indigo-50 border border-indigo-300 text-indigo-900 cursor-pointer"
                  >
                    2 Mo
                  </button>
                  <button
                    type="button"
                    onClick={() => applyPresetCount(4, true)}
                    className="px-2 py-0.5 text-[10px] font-extrabold uppercase rounded bg-white hover:bg-indigo-50 border border-indigo-300 text-indigo-900 cursor-pointer"
                  >
                    4 Mo
                  </button>
                </div>
              </div>

              {/* Rows List */}
              <div className="space-y-2.5">
                {splitRows.map((row, idx) => {
                  const rowAmt = parseFloat(row.amount) || 0;
                  const rowPct = lineAmount > 0 ? ((rowAmt / lineAmount) * 100).toFixed(1) : "0.0";
                  return (
                    <div
                      key={idx}
                      className="p-2.5 bg-white border border-indigo-200 rounded-lg flex flex-wrap items-center gap-2.5 sm:gap-3"
                    >
                      <span className="text-[11px] font-black text-indigo-900 w-5">
                        #{idx + 1}
                      </span>

                      <div className="min-w-[140px] flex-1">
                        <select
                          value={row.month}
                          onChange={(e) => handleSplitMonthChange(idx, e.target.value)}
                          className="w-full text-xs font-bold text-slate-900 bg-slate-50 border border-slate-300 rounded px-2 py-1.5 focus:border-indigo-500 focus:bg-white"
                        >
                          {FY_MONTHS.map((m) => (
                            <option key={m} value={m}>
                              {formatMonthLabel(m)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="w-36 flex items-center gap-1">
                        <span className="text-xs font-bold text-slate-500">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={row.amount}
                          onChange={(e) => handleSplitAmountChange(idx, e.target.value)}
                          className="w-full text-xs font-extrabold tabular text-slate-900 bg-slate-50 border border-slate-300 rounded px-2 py-1.5 focus:border-indigo-500 focus:bg-white"
                        />
                      </div>

                      <div className="w-16 text-right">
                        <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200">
                          {rowPct}%
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeSplitRow(idx)}
                        disabled={splitRows.length <= 1}
                        className="text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed p-1"
                        title="Remove month"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Split Actions */}
              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                <button
                  type="button"
                  onClick={addSplitRow}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg border border-indigo-300 bg-white hover:bg-indigo-50 text-indigo-900 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Month
                </button>
                <button
                  type="button"
                  onClick={() => distributeEvenly()}
                  className="px-3 py-1.5 text-xs font-bold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 cursor-pointer"
                >
                  Distribute Evenly
                </button>
              </div>

              {/* Reconciliation Balance Bar */}
              <div
                className={`p-3 rounded-lg border text-xs flex flex-wrap items-center justify-between gap-2 ${
                  isSplitBalanced
                    ? "bg-emerald-50/80 border-emerald-300 text-emerald-950"
                    : "bg-rose-50/80 border-rose-300 text-rose-950"
                }`}
              >
                <div className="flex items-center gap-2 font-bold">
                  {isSplitBalanced ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-600" />
                  )}
                  <span>
                    Total: <strong className="tabular">{money(lineAmount)}</strong> ·
                    Allocated: <strong className="tabular">{money(splitTotal)}</strong>
                  </span>
                </div>

                <div className="text-[11px] font-black">
                  {isSplitBalanced ? (
                    <span className="text-emerald-700">✓ 100% Balanced</span>
                  ) : (
                    <span className="text-rose-700">
                      Remaining to allocate: {money(splitRemaining)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reason / Note Field */}
          <div>
            <label className="block text-xs font-black uppercase tracking-wider text-slate-700 mb-1.5">
              Reason / Note (Optional):
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Q2 Salon Rent (July, August, September 2026) or Sep power bill paid in Oct"
              className="w-full text-xs font-medium text-slate-900 bg-white border border-slate-300 rounded-lg px-3 py-2 outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-5 sm:px-6 py-3.5 bg-slate-50 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            {existing && (
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-rose-700 hover:text-rose-900 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl transition-all cursor-pointer"
                title="Remove any allocation and revert transaction back to original bank statement date"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset to Bank Date
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold uppercase text-slate-600 hover:text-slate-900 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={mode === "split" && !isSplitBalanced}
              className={`px-5 py-2 text-xs font-bold uppercase rounded-xl shadow-xs transition-all flex items-center gap-1.5 cursor-pointer ${
                mode === "split" && !isSplitBalanced
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-amber-600 hover:bg-amber-700 text-white border border-amber-700"
              }`}
            >
              Save Allocation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
